import React, { useCallback, useState } from 'react'
import { AiResult } from '../../../../../../../../models/AiResult'
import { HeaderResultOption } from './HeaderResultOptionsButton'
import { AiFeature } from '../../../../../../../../models/AiFeature'
import { FeedbackModal } from '../../../../FeedbackModal/FeedbackModal'
import { AddResultDefaultToCRMModal } from '../../../../../../../../views/MeetingSummary/CRM/V2/AddResultDefaultToCRMModal'
import { CUSTOM_PRESET_MAPPING } from '../../../../CustomPresets/CustomPresetMapping'
import { useAppDispatch, useAppSelector } from '../../../../../../../../store/hooks'
import { selectEditingAiResultIds, showToast, startEditingAiResult, stopEditingAiResult } from '../../../../../../../../store/meetingSummary/display'
import { selectSelectedMeetingTemplate } from '../../../../../../../../store/meetingSummary/meetingTemplates'
import { selectCurrentUser } from '../../../../../../../../store/meetingSummary/user'
import { useZocksAuth } from '../../../../../../../../components/ScopedBlock/hooks'
import { TrackEvent, useTracking } from '../../../../../common/tracking'
import { getAIFeatureAndResultTrackingDetails } from '../../../../../tracking'
import { RerunModal } from '../../../../RerunModal/RerunModal'
import { getIntegrationDisplayName, IntegrationServiceId } from '../../../../../../../../common/integrations'
import { selectRoomSessionHasTranscription } from '../../../../../../../../store/meetingSummary/roomSession'
import { IntegrationIcon } from '../../../../../../../../components/IntegrationIcon'

export interface HeaderResultOptionConfig {
  resultIsCollapsed: boolean
  setResultCollapsed: (collapsed: boolean) => void
  hiddenOptions: string[]
}

export const useDefaultHeaderResultOptions = (aiFeature: AiFeature, aiResult: AiResult, config: HeaderResultOptionConfig): HeaderResultOption[] => {
  const rerunHeaderResultOption = useRerunHeaderResultOption(aiFeature, aiResult)
  const editHeaderResultOption = useEditHeaderResultOption(aiFeature, aiResult, config)
  const copyLinkHeaderResultOption = useCopyLinkHeaderResultOption(aiResult)
  const feedbackHeaderResultOption = useFeedbackHeaderResultOption(aiFeature, aiResult)
  const wealthboxHeaderResultOption = useWealthboxHeaderResultOption(aiFeature, aiResult)
  const salesforceHeaderResultOption = useSalesforceHeaderResultOption(aiFeature, aiResult)
  const dynamicsHeaderResultOption = useDynamicsHeaderResultOption(aiFeature, aiResult)
  const engageHeaderResultOption = useEngageHeaderResultOption(aiFeature, aiResult)
  const smartofficeHeaderResultOption = useSmartOfficeHeaderResultOption(aiFeature, aiResult)
  const advisorengineHeaderResultOption = useAdvisorEngineHeaderResultOption(aiFeature, aiResult)
  return [
    rerunHeaderResultOption,
    editHeaderResultOption,
    copyLinkHeaderResultOption,
    feedbackHeaderResultOption,
    wealthboxHeaderResultOption,
    salesforceHeaderResultOption,
    dynamicsHeaderResultOption,
    smartofficeHeaderResultOption,
    engageHeaderResultOption,
    advisorengineHeaderResultOption,
  ].filter((resultOption): resultOption is HeaderResultOption => !!resultOption)
}

export const useRerunHeaderResultOption = (aiFeature: AiFeature, aiResult: AiResult): HeaderResultOption => {
  const [showModal, setShowModal] = useState<boolean>(false)

  const { hasScope } = useZocksAuth()
  const hasAiFeatureExecuteScope = hasScope('**', 'aifeature:execute', ['aifeature:admin'])

  const selectedMeetingTemplate = useAppSelector(selectSelectedMeetingTemplate)

  const handleShowRerunModal = useCallback(() => {
    if (hasAiFeatureExecuteScope) {
      setShowModal(true)
    }
  }, [hasAiFeatureExecuteScope])

  const handleHideRerunModal = useCallback(() => setShowModal(false), [])
  const hasTranscription = useAppSelector(selectRoomSessionHasTranscription)

  return {
    selectOption: {
      id: 'rerun',
      label: 'Rerun',
      disabled: !hasAiFeatureExecuteScope || !selectedMeetingTemplate?.isActive || !hasTranscription,
    },
    icon: 'bi-arrow-counterclockwise',
    handleSelected: handleShowRerunModal,
    children: showModal ? <RerunModal aiFeature={aiFeature} aiResult={aiResult} show={showModal} onHide={handleHideRerunModal} /> : null,
    tooltipLabel: !hasTranscription ? 'Meeting has no transcription' : undefined,
    triggerOverlay: !hasTranscription,
  }
}

export const useEditHeaderResultOption = (aiFeature: AiFeature, aiResult: AiResult, config: HeaderResultOptionConfig): HeaderResultOption | null => {
  const dispatch = useAppDispatch()

  const { trackingClient } = useTracking()
  const { hasScope } = useZocksAuth()
  const hasAiResultUpdateScope = hasScope('**', 'airesult:update', ['airesult:admin'])

  const { resultIsCollapsed, setResultCollapsed, hiddenOptions } = config
  const { id: aiResultId } = aiResult

  const editingAiResultIds = useAppSelector(selectEditingAiResultIds)

  const trackOverrideEvent = useCallback(
    (actionStatus: string) => {
      trackingClient.track({
        eventName: TrackEvent.AI_RESULT_OVERRIDE,
        eventDetails: {
          ...getAIFeatureAndResultTrackingDetails(aiFeature, aiResult),
          actionStatus,
        },
      })
    },
    [trackingClient, aiFeature, aiResult],
  )

  const handleEditClicked = useCallback(() => {
    if (!hasAiResultUpdateScope) {
      return
    }

    if (!editingAiResultIds.includes(aiResultId)) {
      if (resultIsCollapsed) {
        setResultCollapsed(false)
      }
      trackOverrideEvent('started')
      dispatch(startEditingAiResult(aiResultId))
    } else {
      trackOverrideEvent('canceled')
      dispatch(stopEditingAiResult(aiResultId))
    }
  }, [dispatch, trackOverrideEvent, hasAiResultUpdateScope, editingAiResultIds, resultIsCollapsed, aiResultId, setResultCollapsed])

  if (hiddenOptions.includes('edit')) {
    return null
  }

  return {
    selectOption: {
      id: 'edit',
      label: editingAiResultIds.includes(aiResultId) ? 'Cancel editing' : 'Edit',
      disabled: !hasAiResultUpdateScope,
    },
    icon: 'bi-pencil',
    handleSelected: handleEditClicked,
  }
}

export const useCopyLinkHeaderResultOption = (result: AiResult): HeaderResultOption => {
  const dispatch = useAppDispatch()

  const selectedMeetingTemplate = useAppSelector(selectSelectedMeetingTemplate)

  const { aiFeatureId } = result

  const handleCopyLinkToBlockClicked = useCallback(async () => {
    if (!selectedMeetingTemplate) {
      return
    }
    const { id: meetingTemplateId } = selectedMeetingTemplate
    const linkToBlock = `${window.location.href.split('#')[0]}#${aiFeatureId}-${meetingTemplateId}`
    await navigator.clipboard.writeText(linkToBlock)
    dispatch(
      showToast({
        variant: 'success',
        icon: 'bi-check-circle-fill',
        message: 'Link to block copied to clipboard!',
      }),
    )
  }, [dispatch, selectedMeetingTemplate, aiFeatureId])

  return {
    selectOption: {
      id: 'copy-link',
      label: 'Copy link to block',
    },
    icon: 'bi-link-45deg',
    handleSelected: handleCopyLinkToBlockClicked,
  }
}

export const useFeedbackHeaderResultOption = (aiFeature: AiFeature, aiResult: AiResult): HeaderResultOption => {
  const [showModal, setShowModal] = useState<boolean>(false)

  const { hasScope } = useZocksAuth()
  const hasAiResultScoreScope = hasScope('**', 'airesult:score', ['airesult:admin'])

  const handleShowFeedbackModal = useCallback(() => {
    if (hasAiResultScoreScope) {
      setShowModal(true)
    }
  }, [hasAiResultScoreScope])
  const handleHideFeedbackModal = useCallback(() => setShowModal(false), [])

  return {
    selectOption: {
      id: 'feedback',
      label: 'Give us feedback',
      disabled: !hasAiResultScoreScope,
    },
    icon: 'bi-star-half',
    handleSelected: handleShowFeedbackModal,
    children: showModal ? <FeedbackModal aiFeature={aiFeature} aiResult={aiResult} show={showModal} onHide={handleHideFeedbackModal} /> : null,
  }
}

export const useWealthboxHeaderResultOption = (aiFeature: AiFeature, aiResult: AiResult): HeaderResultOption | null => {
  const currentUser = useAppSelector(selectCurrentUser)

  const { integrations: currentUserIntegrations } = currentUser ?? {}
  const { integrationInfos: aiFeatureIntegrations } = aiFeature

  const [showModal, setShowModal] = useState<boolean>(false)

  const handleSendToWealthboxClicked = () => setShowModal(true)

  const userHasWealthboxIntegration = currentUserIntegrations?.some(({ serviceId }) => serviceId === IntegrationServiceId.WEALTHBOX)
  const aiFeatureHasWealthboxIntegration = aiFeatureIntegrations.some(({ serviceId }) => serviceId === IntegrationServiceId.WEALTHBOX)

  if (!userHasWealthboxIntegration || !aiFeatureHasWealthboxIntegration) {
    return null
  }

  const { preset: { id: aiFeaturePresetId = '' } = {} } = aiFeature
  const CRMModal = CUSTOM_PRESET_MAPPING[aiFeaturePresetId]?.crmModal ?? AddResultDefaultToCRMModal
  const displayName = getIntegrationDisplayName(IntegrationServiceId.WEALTHBOX)
  return {
    selectOption: {
      id: 'wealthbox',
      label: `Send to ${displayName}`,
    },
    IconComponent: () => <IntegrationIcon serviceId={IntegrationServiceId.WEALTHBOX} className='wealthbox-icon' fill='var(--zocks-gray-900)' />,
    handleSelected: handleSendToWealthboxClicked,
    className: 'd-flex d-md-none',
    children: showModal ? (
      <CRMModal aiFeature={aiFeature} result={aiResult} show={showModal} onHide={() => setShowModal(false)} serviceId={IntegrationServiceId.WEALTHBOX} />
    ) : null,
  }
}

export const useSalesforceHeaderResultOption = (aiFeature: AiFeature, aiResult: AiResult): HeaderResultOption | null => {
  const currentUser = useAppSelector(selectCurrentUser)
  const { integrations: currentUserIntegrations } = currentUser ?? {}
  const { integrationInfos: aiFeatureIntegrations } = aiFeature

  const [showModal, setShowModal] = useState<boolean>(false)

  const handleSendToSalesforceClicked = () => setShowModal(true)

  const userHasSalesForceIntegration = currentUserIntegrations?.some(({ serviceId }) => serviceId === IntegrationServiceId.SALESFORCE)
  const aiFeatureHasSalesForceIntegration = aiFeatureIntegrations.some(({ serviceId }) => serviceId === IntegrationServiceId.SALESFORCE)

  if (!userHasSalesForceIntegration || !aiFeatureHasSalesForceIntegration) {
    return null
  }

  const { preset: { id: aiFeaturePresetId = '' } = {} } = aiFeature
  const CRMModal = CUSTOM_PRESET_MAPPING[aiFeaturePresetId]?.crmModal ?? AddResultDefaultToCRMModal
  const displayName = getIntegrationDisplayName(IntegrationServiceId.SALESFORCE)
  return {
    selectOption: {
      id: 'salesforce',
      label: `Send to ${displayName}`,
    },
    IconComponent: () => <IntegrationIcon serviceId={IntegrationServiceId.SALESFORCE} className='salesforce-icon' fill='var(--zocks-gray-900)' />,
    handleSelected: handleSendToSalesforceClicked,
    className: 'd-flex d-md-none',
    children: showModal ? (
      <CRMModal aiFeature={aiFeature} result={aiResult} show={showModal} onHide={() => setShowModal(false)} serviceId={IntegrationServiceId.SALESFORCE} />
    ) : null,
  }
}

export const useZohoHeaderResultOption = (aiFeature: AiFeature, aiResult: AiResult): HeaderResultOption | null => {
  const currentUser = useAppSelector(selectCurrentUser)
  const { integrations: currentUserIntegrations } = currentUser ?? {}
  const { integrationInfos: aiFeatureIntegrations } = aiFeature

  const [showModal, setShowModal] = useState<boolean>(false)

  const handleSendToZohoClicked = () => setShowModal(true)

  const userHasZohoIntegration = currentUserIntegrations?.some(({ serviceId }) => serviceId === IntegrationServiceId.ZOHO)
  const aiFeatureZohoIntegration = aiFeatureIntegrations.some(({ serviceId }) => serviceId === IntegrationServiceId.ZOHO)

  if (!userHasZohoIntegration || !aiFeatureZohoIntegration) {
    return null
  }

  const { preset: { id: aiFeaturePresetId = '' } = {} } = aiFeature
  const CRMModal = CUSTOM_PRESET_MAPPING[aiFeaturePresetId]?.crmModal ?? AddResultDefaultToCRMModal
  const displayName = getIntegrationDisplayName(IntegrationServiceId.ZOHO)
  return {
    selectOption: {
      id: 'zoho',
      label: `Send to ${displayName}`,
    },
    IconComponent: () => <IntegrationIcon serviceId={IntegrationServiceId.ZOHO} className='zoho-icon' fill='var(--zocks-gray-900)' />,
    handleSelected: handleSendToZohoClicked,
    className: 'd-flex d-md-none',
    children: showModal ? (
      <CRMModal aiFeature={aiFeature} result={aiResult} show={showModal} onHide={() => setShowModal(false)} serviceId={IntegrationServiceId.ZOHO} />
    ) : null,
  }
}

export const useDynamicsHeaderResultOption = (aiFeature: AiFeature, aiResult: AiResult): HeaderResultOption | null => {
  const currentUser = useAppSelector(selectCurrentUser)
  const { integrations: currentUserIntegrations } = currentUser ?? {}
  const { integrationInfos: aiFeatureIntegrations } = aiFeature

  const [showModal, setShowModal] = useState<boolean>(false)

  const handleSendToDynamicsClicked = () => setShowModal(true)

  const userHasDynamicsIntegration = currentUserIntegrations?.some(({ serviceId }) => serviceId === IntegrationServiceId.DYNAMICS)
  const aiFeatureHasDynamicsIntegration = aiFeatureIntegrations.some(({ serviceId }) => serviceId === IntegrationServiceId.DYNAMICS)

  if (!userHasDynamicsIntegration || !aiFeatureHasDynamicsIntegration) {
    return null
  }

  const { preset: { id: aiFeaturePresetId = '' } = {} } = aiFeature
  const CRMModal = CUSTOM_PRESET_MAPPING[aiFeaturePresetId]?.crmModal ?? AddResultDefaultToCRMModal
  const displayName = getIntegrationDisplayName(IntegrationServiceId.DYNAMICS)
  return {
    selectOption: {
      id: 'dynamics',
      label: `Send to ${displayName}`,
    },
    IconComponent: () => <IntegrationIcon serviceId={IntegrationServiceId.DYNAMICS} className='crm-icon' fill='var(--zocks-gray-900)' />,
    handleSelected: handleSendToDynamicsClicked,
    className: 'd-flex d-md-none',
    children: showModal ? (
      <CRMModal aiFeature={aiFeature} result={aiResult} show={showModal} onHide={() => setShowModal(false)} serviceId={IntegrationServiceId.DYNAMICS} />
    ) : null,
  }
}

export const useEngageHeaderResultOption = (aiFeature: AiFeature, aiResult: AiResult): HeaderResultOption | null => {
  const currentUser = useAppSelector(selectCurrentUser)
  const { integrations: currentUserIntegrations } = currentUser ?? {}
  const { integrationInfos: aiFeatureIntegrations } = aiFeature

  const [showModal, setShowModal] = useState<boolean>(false)

  const handleSendToEngageClicked = () => setShowModal(true)

  const userHasEngageIntegration = currentUserIntegrations?.some(({ serviceId }) => serviceId === IntegrationServiceId.ENGAGE)
  const aiFeatureHasEngageIntegration = aiFeatureIntegrations.some(({ serviceId }) => serviceId === IntegrationServiceId.ENGAGE)

  if (!userHasEngageIntegration || !aiFeatureHasEngageIntegration) {
    return null
  }

  const { preset: { id: aiFeaturePresetId = '' } = {} } = aiFeature
  const CRMModal = CUSTOM_PRESET_MAPPING[aiFeaturePresetId]?.crmModal ?? AddResultDefaultToCRMModal
  const displayName = getIntegrationDisplayName(IntegrationServiceId.ENGAGE)
  return {
    selectOption: {
      id: 'engage',
      label: `Send to ${displayName}`,
    },
    IconComponent: () => <IntegrationIcon serviceId={IntegrationServiceId.ENGAGE} className='crm-icon' fill='var(--zocks-gray-900)' />,
    handleSelected: handleSendToEngageClicked,
    className: 'd-flex d-md-none',
    children: showModal ? (
      <CRMModal aiFeature={aiFeature} result={aiResult} show={showModal} onHide={() => setShowModal(false)} serviceId={IntegrationServiceId.ENGAGE} />
    ) : null,
  }
}

export const useSmartOfficeHeaderResultOption = (aiFeature: AiFeature, aiResult: AiResult): HeaderResultOption | null => {
  const currentUser = useAppSelector(selectCurrentUser)
  const { integrations: currentUserIntegrations } = currentUser ?? {}
  const { integrationInfos: aiFeatureIntegrations } = aiFeature

  const [showModal, setShowModal] = useState<boolean>(false)

  const handleSendToSmartOfficeClicked = () => setShowModal(true)

  const userHasSmartOfficeIntegration = currentUserIntegrations?.some(({ serviceId }) => serviceId === IntegrationServiceId.SMARTOFFICE)
  const aiFeatureHasSmartOfficeIntegration = aiFeatureIntegrations.some(({ serviceId }) => serviceId === IntegrationServiceId.SMARTOFFICE)

  if (!userHasSmartOfficeIntegration || !aiFeatureHasSmartOfficeIntegration) {
    return null
  }

  const { preset: { id: aiFeaturePresetId = '' } = {} } = aiFeature
  const CRMModal = CUSTOM_PRESET_MAPPING[aiFeaturePresetId]?.crmModal ?? AddResultDefaultToCRMModal
  const displayName = getIntegrationDisplayName(IntegrationServiceId.SMARTOFFICE)
  return {
    selectOption: {
      id: 'smartoffice',
      label: `Send to ${displayName}`,
    },
    IconComponent: () => <IntegrationIcon serviceId={IntegrationServiceId.SMARTOFFICE} className='crm-icon' fill='var(--zocks-gray-900)' />,
    handleSelected: handleSendToSmartOfficeClicked,
    className: 'd-flex d-md-none',
    children: showModal ? (
      <CRMModal aiFeature={aiFeature} result={aiResult} show={showModal} onHide={() => setShowModal(false)} serviceId={IntegrationServiceId.SMARTOFFICE} />
    ) : null,
  }
}

export const useAdvisorEngineHeaderResultOption = (aiFeature: AiFeature, aiResult: AiResult): HeaderResultOption | null => {
  const currentUser = useAppSelector(selectCurrentUser)
  const { integrations: currentUserIntegrations } = currentUser ?? {}
  const { integrationInfos: aiFeatureIntegrations } = aiFeature

  const [showModal, setShowModal] = useState<boolean>(false)

  const handleSendToAdvisorEngineClicked = () => setShowModal(true)

  const userHasAdvisorEngineIntegration = currentUserIntegrations?.some(({ serviceId }) => serviceId === IntegrationServiceId.ADVISOR_ENGINE)
  const aiFeatureHasAdvisorEngineIntegration = aiFeatureIntegrations.some(({ serviceId }) => serviceId === IntegrationServiceId.ADVISOR_ENGINE)

  if (!userHasAdvisorEngineIntegration || !aiFeatureHasAdvisorEngineIntegration) {
    return null
  }

  const { preset: { id: aiFeaturePresetId = '' } = {} } = aiFeature
  const CRMModal = CUSTOM_PRESET_MAPPING[aiFeaturePresetId]?.crmModal ?? AddResultDefaultToCRMModal
  const displayName = getIntegrationDisplayName(IntegrationServiceId.ADVISOR_ENGINE)
  return {
    selectOption: {
      id: 'advisorengine',
      label: `Send to ${displayName}`,
    },
    IconComponent: () => <IntegrationIcon serviceId={IntegrationServiceId.ADVISOR_ENGINE} className='crm-icon' fill='var(--zocks-gray-900)' />,
    handleSelected: handleSendToAdvisorEngineClicked,
    className: 'd-flex d-md-none',
    children: showModal ? (
      <CRMModal aiFeature={aiFeature} result={aiResult} show={showModal} onHide={() => setShowModal(false)} serviceId={IntegrationServiceId.ADVISOR_ENGINE} />
    ) : null,
  }
}
